import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Waves, Printer, ArrowRight, FlaskConical, Layers, FileText, Ruler, Boxes, Mail, Copy, Check, Phone, Building2, Wrench, PackageCheck } from "lucide-react";
import Logo from "../components/Logo";
import { CONTACT, ROADMAP, COMMERCIAL, TESTER_IMG, DIM } from "../data/build";
import { IMG } from "../data/content";
import { OUTREACH } from "../components/shop/OutreachEmail";

const SPEC = [
  ["Envelope", `${DIM.w} × ${DIM.h} × ${DIM.d} mm`],
  ["Weight", DIM.weight],
  ["Screen", DIM.screen],
  ["Probes", DIM.probes],
  ["Strips", DIM.strips],
  ["Battery", DIM.battery],
  ["Panel time", DIM.panel],
];

const DOCS = [
  { to: "/shop/build", icon: Ruler, name: "Build Spec", desc: "Full exterior GA drawings, sensing architecture, variants and the 24-part component schedule." },
  { to: "/shop/interior", icon: Boxes, name: "Interior Spec", desc: "To-scale interior drawings and the per-component catalogue." },
  { to: "/shop/brief", icon: FileText, name: "Manufacturing Brief / RFQ", desc: "The printable brief with everything we'd like quoted, stage by stage." },
];

export default function Manufacturer() {
  const [copied, setCopied] = useState(false);
  const v = OUTREACH.manufacturer;
  const body = v.body;
  const mailto = `mailto:?subject=${encodeURIComponent(v.subject)}&body=${encodeURIComponent(body)}`;
  const copy = async () => {
    const full = `Subject: ${v.subject}\n\n${body}`;
    try { await navigator.clipboard.writeText(full); }
    catch (e) { const ta = document.createElement("textarea"); ta.value = full; document.body.appendChild(ta); ta.select(); try { document.execCommand("copy"); } catch (err) {} document.body.removeChild(ta); }
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="min-h-screen bg-[#FAFCFD] text-slate-900" data-testid="manufacturer-page" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
      <header className="no-print sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline font-mono2 text-[10px] uppercase tracking-widest text-slate-400">Build partner · confidential</span>
            <button onClick={() => window.print()} data-testid="mfr-print-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors"><Printer className="h-4 w-4" /> Print / PDF</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50 via-white to-white" />
        <div className="mx-auto max-w-6xl px-5 lg:px-8 pt-16 md:pt-24 pb-12">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-6"><Waves className="h-3.5 w-3.5" /> Invitation to a product-build partner</span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.03]">Let's build the <span className="aqua-text">Aqualux Nexus</span> tester.</h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-600">A reagentless benchtop &amp; portable pool-water tester — four continuous probes plus three optical strip readers, a full water panel in ~60–90 seconds. Fully designed and specified, ready for a manufacturing partner to bring to life.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#docs" data-testid="mfr-docs-cta" className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white font-semibold px-6 py-3 transition-colors">See the build pack <ArrowRight className="h-4 w-4" /></a>
              <a href="#invite" className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-6 py-3 transition-colors">The RFQ invite</a>
            </div>
          </motion.div>
          <div className="mt-12 grid md:grid-cols-2 gap-6 items-center">
            <img src={TESTER_IMG.front} alt="Pool Shop tester" className="w-full rounded-2xl border border-slate-200 shadow-lg" />
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="font-display font-bold flex items-center gap-2"><FlaskConical className="h-4 w-4 text-sky-600" /> Headline specification</p>
              <dl className="mt-4 divide-y divide-slate-100">
                {SPEC.map(([k, val]) => (
                  <div key={k} className="flex items-center justify-between py-2 text-sm"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-slate-800 text-right">{val}</dd></div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* What we need built */}
      <section id="docs" className="px-5 lg:px-8 py-16 md:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-sky-700">The build pack</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Everything's drawn and specified.</h2>
          <p className="mt-3 text-slate-600 max-w-2xl">Open any document below — full engineering detail, component schedules and the RFQ are ready for your review.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {DOCS.map((d) => { const Icon = d.icon; return (
              <Link key={d.to} to={d.to} data-testid={`mfr-doc-${d.name.split(" ")[0].toLowerCase()}`} className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-sky-300 hover:shadow-lg transition-all">
                <span className="h-10 w-10 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center"><Icon className="h-5 w-5 text-sky-600" /></span>
                <h3 className="font-display font-bold mt-3">{d.name}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{d.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sky-700 font-semibold text-sm group-hover:gap-2.5 transition-all">Open <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ); })}
          </div>
        </div>
      </section>

      {/* The build path + quoting bands */}
      <section className="px-5 lg:px-8 py-16 md:py-20 bg-[#0b1f36] text-white">
        <div className="mx-auto max-w-6xl">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-sky-300">The opportunity to grow together</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 text-white">One device now — a product family next.</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {ROADMAP.map((r) => (
              <div key={r.step} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <span className="font-mono2 text-xs text-sky-300">STEP {r.step}</span>
                <h4 className="font-display font-bold mt-1 text-white">{r.name}</h4>
                <p className="mt-1.5 text-sm text-sky-100/70">{r.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6">
            <p className="flex items-center gap-2 font-display font-bold text-white"><PackageCheck className="h-4 w-4 text-sky-300" /> Please quote against these volume bands</p>
            <p className="text-sm text-sky-100/70 mt-1">{COMMERCIAL.bandsNote}</p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {COMMERCIAL.bands.map((b) => (
                <div key={b.k} className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-center"><p className="font-display font-extrabold text-white">{b.v}</p><p className="text-[11px] text-sky-200/70 mt-0.5">{b.k}</p></div>
              ))}
            </div>
            <p className="mt-4 text-sm text-sky-100/70">{COMMERCIAL.rollout}</p>
          </div>
        </div>
      </section>

      {/* Invite */}
      <section id="invite" className="px-5 lg:px-8 py-16 md:py-20">
        <div className="mx-auto max-w-6xl grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-sky-700">The RFQ invite</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Ready to talk to a manufacturer.</h2>
            <p className="mt-4 text-slate-600">A ready-to-send first-contact message inviting a manufacturer to quote the build. Add their name, paste and send — or open it straight in your email app.</p>
            <div className="mt-6 space-y-2 text-slate-700">
              <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-sky-600" /> {CONTACT.name} · {CONTACT.company}</p>
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2 hover:text-slate-900"><Phone className="h-4 w-4 text-sky-600" /> {CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-slate-900"><Mail className="h-4 w-4 text-sky-600" /> {CONTACT.email}</a>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
            <p className="font-display font-bold flex items-center gap-2"><Wrench className="h-4 w-4 text-sky-600" /> Manufacturer invitation email</p>
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 max-h-64 overflow-auto">
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">Subject</p>
              <p className="text-sm text-slate-800 font-medium">{v.subject}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mt-3">Body</p>
              <pre className="whitespace-pre-wrap font-sans text-[13px] text-slate-700 leading-relaxed mt-1" data-testid="mfr-invite-body">{body}</pre>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={mailto} data-testid="mfr-invite-mailto" className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold px-5 py-2.5 transition-colors"><Mail className="h-4 w-4" /> Open in email app</a>
              <button onClick={copy} data-testid="mfr-invite-copy" className={`inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2.5 transition-colors ${copied ? "bg-emerald-600 text-white" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}</button>
            </div>
          </div>
        </div>
      </section>

      <footer className="px-5 lg:px-8 py-10 border-t border-slate-200">
        <div className="mx-auto max-w-6xl flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
          <span className="flex items-center gap-2"><Layers className="h-4 w-4" /> Aqualux Nexus · Confidential build pack. No patent/IP material included in this view.</span>
          <Link to="/" className="hover:text-slate-600">Back to hub</Link>
        </div>
      </footer>
    </div>
  );
}
